<template>
  <div class="editor" v-loading="loading">
    <div style="border: 1px solid #ccc">
      <Toolbar style="border-bottom: 1px solid #ccc" :editor="editor" :defaultConfig="toolbarConfig" :mode="mode" />
      <Editor :style="{ height: h + 'px' }" v-model="html" :defaultConfig="editorConfig" :mode="mode" @onCreated="onCreated" @onChange="onChange" />
    </div>
  </div>
</template>

<script>
import { Editor, Toolbar } from "@wangeditor/editor-for-vue"
import { Message } from "element-ui"

export default {
  components: { Editor, Toolbar },
  props: {
    img: { type: Boolean, default: true },
    htmls: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
    h: { type: Number, default: 500, validator: v => v >= 310 },
  },
  data() {
    const MAX_IMAGE_SIZE = 10 * 1024 * 1024 // 10MB
    const MAX_VIDEO_SIZE = 200 * 1024 * 1024 // 200MB
    return {
      loading: false,
      editor: null,
      html: "",
      toolbarConfig: {
        excludeKeys: [this.img ? "" : "group-image", "emotion"],
      },

      // 编辑器核心配置
      editorConfig: {
        placeholder: "请输入内容...",
        // 覆盖默认的上传配置（关键）
        MENU_CONF: {
          // 视频上传配置
          uploadVideo: {
            // server: this.$axios.defaults.baseURL + this.$api.uploadRTFVideoFile, // 视频上传接口地址
            server: this.$axios.defaults.baseURL + this.$api.uploadFile, // 视频上传接口地址
            fieldName: "multipartFile", // 上传字段名（根据后端要求修改）
            timeout: 30000, // 超时时间（视频较大，可适当延长）
            // 单个文件的最大体积限制，默认为 10M
            // maxFileSize: 500 * 1024 * 1024, // 200MB
            maxFileSize: MAX_VIDEO_SIZE,
            // 视频格式限制（可选）
            allowedFileTypes: ["video/mp4", "video/ogg", "video/webm"],
            onBeforeUpload: (file, insertFn, uploadFn) => {
              const getRealVideoFile = wrapFile => {
                if (wrapFile instanceof File || wrapFile instanceof Blob) {
                  return wrapFile
                }
                const possibleProps = [...Object.keys(wrapFile).filter(key => key.startsWith("uppy-")), "data", "file", "originalFile", "sourceFile"]

                for (const prop of possibleProps) {
                  const value = wrapFile[prop]
                  if (value instanceof File || value instanceof Blob) {
                    // console.log("找到真实文件，所在属性：", prop);
                    return value
                  }
                  if (typeof value === "object" && value !== null) {
                    const nestedFile = getRealVideoFile(value)
                    if (nestedFile instanceof File || nestedFile instanceof Blob) {
                      return nestedFile
                    }
                  }
                }
                return {
                  name: wrapFile.name || "未知视频.mp4",
                  size: wrapFile.size || 0,
                  type: wrapFile.type || "video/mp4",
                }
              }
              // 提取真实文件（关键步骤）
              const realFile = getRealVideoFile(file)
              // console.log("最终用于校验的真实文件：", realFile);
              // console.log("最终大小：", realFile.size > MAX_VIDEO_SIZE);
              // console.log("最终大小：", realFile.size);
              // console.log(realFile.size);
              // 校验是否超过200MB
              if (realFile.size > MAX_VIDEO_SIZE) {
                Message.error(`视频超过200MB限制，请压缩后上传`)
                return false
              }
              // 视频格式校验
              const isVideo = /\.(mp4|ogg|webm)$/i.test(realFile.name)
              if (!isVideo) {
                Message.error(`视频 ${file.name} 格式不正确，仅支持mp4、ogg、webm`)
                return false
              }

              return true
            },
            // 捕获Editor内置大小超限
            onError: (file, errType, res) => {
              // console.error("上传错误详情：", errType, res);
              // 找到真实文件名（兼容包装结构）
              const realFileName = file.name || Object.values(file).find(v => v instanceof File)?.name || "未知视频"

              // 替换内置的大小超限提示
              if (errType === "exceed-size" || (res?.message && res.message.includes("exceeds maximum allowed size"))) {
                Message.error(`视频《${realFileName}》超过200MB限制，请压缩后上传`)
              }
              // 其他错误提示
              else if (errType === "not-allowed-type") {
                Message.error(`视频《${realFileName}》格式不正确，仅支持mp4、ogg、webm格式`)
              } else if (errType === "network") {
                Message.error(`视频《${realFileName}》上传失败，网络异常`)
              } else {
                // Message.error(`视频《${realFileName}》上传失败：${res?.message || res?.desc || "未知错误"}`);
                Message.error(`视频《${realFileName}》上传失败：${res?.message || res?.desc || "超过200MB限制，请压缩后上传"}`)
              }
            },
            // 上传成功后的处理
            customInsert: (res, insertFn) => {
              if (res.code == 100) {
                // 假设后端返回 code=100 为成功
                insertFn(res.result.fileUrl) // 插入视频地址
              } else {
                // this.$message.error(res.desc)
                Message.error(`视频上传失败：${res.desc || "未知错误"}`)
              }
            },
          },
          // 上传图片配置
          uploadImage: {
            server: this.$axios.defaults.baseURL + this.$api.uploadFile,
            fieldName: "multipartFile",
            timeout: 10000,
            maxFileSize: MAX_IMAGE_SIZE, // 10MB
            maxSize: MAX_IMAGE_SIZE,
            autoCheckSize: false,
            beforeUpload: file => {
              // 1. 先检查文件大小
              if (file.size > MAX_IMAGE_SIZE) {
                Message.error(`图片 ${file.name} 超过10MB限制，请压缩后上传`)
                return false
              }
              const isImage = /\.(jpg|jpeg|png|gif)$/i.test(file.name)
              if (!isImage) {
                Message.error(`图片 ${file.name} 格式不正确，仅支持jpg、jpeg、png、gif`)
                return false
              }
              return true
            },
            customInsert: (res, insertFn) => {
              if (res.code === 100) {
                insertFn(res.result.fileUrl)
              } else {
                Message.error(`图片上传失败：${res.desc || "未知错误"}`)
              }
            },
            onError: (file, err, res) => {
              if (err === "exceed-size" || (res?.desc && res.desc.includes("大小"))) {
                Message.error(`图片 ${file.name} 超过10MB限制，请压缩后上传`)
              } else if (err === "network") {
                Message.error(`图片 ${file.name} 上传失败，网络错误`)
              } else {
                Message.error(`上传失败，图片超过10MB限制`)
              }
            },
          },
        },
      },
      mode: "default",
    }
  },
  mounted() {
    this.loading = true
    setTimeout(() => {
      this.html = this.htmls
      this.loading = false
    }, 1000)
  },
  beforeDestroy() {
    if (this.editor) {
      this.editor.destroy()
    }
  },
  methods: {
    onCreated(editor) {
      this.editor = Object.seal(editor)
      const uploadImageConf = this.editor.getMenuConfig("uploadImage")
      if (uploadImageConf) {
        uploadImageConf.maxSize = this.MAX_IMAGE_SIZE
        uploadImageConf.autoCheckSize = false
      }
      // 处理禁用状态
      this.disabled ? this.editor.disable() : this.editor.enable()
    },
    onChange(editor) {
      this.$emit("change", editor.getHtml())
    },
  },
}
</script>

<style scoped lang="scss">
.editor {
}
</style>
